/**
 * Rambo Ring Widget
 * Replaces native Shopify variant selector with custom ring style swatches + size dropdown
 * Uses data from Rambo Ring backend API
 */

(function() {
  'use strict';

  // Shared state
  const widgetState = {
    selectedStyleIndex: -1,
    selectedSizeCode: ''
  };
  
  // Expose state globally for favorites button and other scripts
  window.ramboRingWidgetState = widgetState;

  // Configuration - update RAMBO_API_BASE_URL to match your backend
  // You can set this via a Liquid variable in main-product.liquid if needed
  const RAMBO_API_BASE_URL = window.RAMBO_API_BASE_URL || 'https://rambo-ring-app-production.up.railway.app'; // Default to Railway production URL
  
  // Find the main product section (not header or other sections)
  function findProductSection() {
    // Try to find main-product section specifically by ID pattern
    let section = document.querySelector('[id*="main-product"], [id*="main_product"]');
    
    // If not found, look for any section with product-form or product-info (but not header/footer)
    if (!section) {
      const allSections = document.querySelectorAll('[id^="shopify-section-"]');
      for (const s of allSections) {
        // Skip header, footer, cart, and other non-product sections
        const sectionId = s.id.toLowerCase();
        if (sectionId.includes('header') || sectionId.includes('footer') || sectionId.includes('cart') || sectionId.includes('nav')) {
          continue;
        }
        // Check if this section has product-related content
        if (s.querySelector('product-form, .product-info, .product, [class*="product"]')) {
          section = s;
          break;
        }
      }
    }
    
    // Final fallback: find any section that's not header/footer and has a form with input[name="id"]
    if (!section) {
      const allSections = document.querySelectorAll('[id^="shopify-section-"]');
      for (const s of allSections) {
        const sectionId = s.id.toLowerCase();
        if (sectionId.includes('header') || sectionId.includes('footer') || sectionId.includes('cart') || sectionId.includes('nav')) {
          continue;
        }
        // Check if it has a product form
        if (s.querySelector('form input[name="id"]')) {
          section = s;
          break;
        }
      }
    }
    
    return section?.id || null;
  }
  
  // Get section ID - will be set when widget initializes
  let SECTION_ID = null;

  /**
   * Helper to clean ring size label (remove "US" and parentheses)
   */
  function cleanRingSizeLabel(label) {
    let cleanLabel = label || '';
    // Remove "US" (case-insensitive)
    cleanLabel = cleanLabel.replace(/\s*US\s*/gi, ' ').trim();
    // Remove parentheses and their contents
    cleanLabel = cleanLabel.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
    // Extract just the number if there's still text
    const numberMatch = cleanLabel.match(/(\d+\.?\d*)/);
    if (numberMatch) {
      cleanLabel = numberMatch[1];
    }
    return cleanLabel;
  }

  /**
   * Initialize the widget
   */
  function initRamboRingWidget() {
    // RESET widget state for each product page load (important for back/forward navigation)
    widgetState.selectedStyleIndex = -1;
    widgetState.selectedSizeCode = '';
    
    // Find the correct product section first
    SECTION_ID = findProductSection();
    if (!SECTION_ID) {
      return;
    }
    
    const productHandle = window.Shopify?.routes?.product || 
                         document.querySelector('[data-product-handle]')?.dataset?.productHandle ||
                         window.location.pathname.split('/products/')[1]?.split('?')[0];

    if (!productHandle) {
      return;
    }

    // Get ring config from Shopify metafield (set by Liquid in theme)
    // Liquid should set: window.RAMBO_RING_CONFIG = {{ product.metafields.rambo.ring_config.value | json }};
    let ringConfig = null;
    
    if (window.RAMBO_RING_CONFIG) {
      ringConfig = window.RAMBO_RING_CONFIG;
    } else if (window.Shopify?.analytics?.meta?.product?.metafields?.rambo?.ring_config) {
      try {
        ringConfig = JSON.parse(window.Shopify.analytics.meta.product.metafields.rambo.ring_config.value);
      } catch (e) {
        // Silent fail
      }
    }
    
    if (!ringConfig) {
      return;
    }

    // Process the ring config immediately (no async fetch needed)
    processRingConfig(ringConfig);
  }
  
  function processRingConfig(ringConfig) {
    // Hide native variant picker
    hideNativeVariantPicker();

    // Render custom widget
    renderRingWidget(ringConfig);

    // Override Add to Cart - wait a bit for form to be ready
    setTimeout(() => {
      overrideAddToCart(ringConfig);
    }, 100);
  }

  /**
   * Hide the native Shopify variant picker
   */
  function hideNativeVariantPicker() {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    // Hide variant picker fieldsets
    const variantPickers = sectionEl.querySelectorAll('fieldset.variant-picker__option');
    variantPickers.forEach(fieldset => {
      fieldset.style.display = 'none';
    });

    // Hide variant picker container if it exists
    const variantPickerContainer = sectionEl.querySelector('variant-picker');
    if (variantPickerContainer) {
      variantPickerContainer.style.display = 'none';
    }

    // Also hide any variant picker by class or ID
    const variantPickerByClass = sectionEl.querySelector('.variant-picker');
    if (variantPickerByClass) {
      variantPickerByClass.style.display = 'none';
    }

    // Hide any product form variant selectors
    const productForm = sectionEl.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInputs = productForm.querySelectorAll('input[name="id"], select[name="id"]');
      variantInputs.forEach(input => {
        // Keep the input but hide its container
        const container = input.closest('.product-form__input, .variant-picker, fieldset');
        if (container) {
          container.style.display = 'none';
        }
      });
    }
  }

  /**
   * Render the custom ring widget (swatches + size dropdown)
   */
  function renderRingWidget(ringConfig) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    // Find the engraving area - we want to insert RIGHT BEFORE it
    // Wait a bit for it to be rendered if not found immediately
    let engravingArea = sectionEl.querySelector('.ring-engraving');
    
    // If not found, wait a bit and try again (engraving might be rendered after variant picker)
    if (!engravingArea) {
      // Try waiting for it to appear (max 1 second)
      let attempts = 0;
      const checkForEngraving = setInterval(() => {
        engravingArea = sectionEl.querySelector('.ring-engraving');
        if (engravingArea || attempts >= 20) { // 20 attempts * 50ms = 1 second max
          clearInterval(checkForEngraving);
          if (engravingArea) {
            insertWidget(ringConfig, engravingArea, sectionEl);
          } else {
            // Fallback if engraving never appears
            insertWidget(ringConfig, null, sectionEl);
          }
        }
        attempts++;
      }, 50);
      return; // Exit early, will be called again when engraving is found
    }
    
    // Engraving found immediately, insert widget
    insertWidget(ringConfig, engravingArea, sectionEl);
  }

  /**
   * Actually insert the widget into the DOM
   */
  function insertWidget(ringConfig, engravingArea, sectionEl) {
    // REMOVE existing widget first (important for re-initialization)
    const existingWidget = sectionEl.querySelector('.rambo-ring-widget');
    if (existingWidget) {
      existingWidget.remove();
    }
    
    // If engraving area exists, insert before it; otherwise fall back to variant picker or form
    let insertTarget = null;
    if (engravingArea) {
      insertTarget = engravingArea; // We'll use insertBefore on this
    } else {
      // Fallback: try to find variant picker or product form
      insertTarget = sectionEl.querySelector('variant-picker') || 
                     sectionEl.querySelector('.product-form') ||
                     sectionEl.querySelector('[data-product-form]') ||
                     sectionEl;
    }

                // Create widget container
                const widgetContainer = document.createElement('div');
                widgetContainer.className = 'rambo-ring-widget';
                widgetContainer.id = 'rambo-ring-widget-' + SECTION_ID;
                // Add inline style to ensure width matches engraving fields
                widgetContainer.style.width = '100%';

    // Build HTML
    let html = '<div class="rambo-ring-widget__container">';

    // Ring Styles Swatches
    html += '<div class="ring-style-picker" id="rambo-ring-style-picker">';
    html += '<div class="ring-style-picker__label">';
    html += 'Ring Styles <span class="ring-style-picker__required">*</span>';
    html += ' <span class="ring-style-picker__selected-name" id="rambo-ring-style-selected-name"></span>';
    html += '</div>';
    html += '<div class="ring-style-picker__swatches">';

    ringConfig.styles.forEach((style, index) => {
      // Don't set active class in HTML - let JavaScript handle it based on URL params
      html += `<button type="button" class="ring-style-picker__swatch" data-style-index="${index}" data-style-label="${escapeHtml(style.label)}">`;
      
      let swatchSrc = style.swatchImage || '';
      
      if (swatchSrc) {
        // Check if this is an S3 URL (doesn't support resizing) or Shopify CDN (supports resizing)
        const isS3 = swatchSrc.includes('s3.') || swatchSrc.includes('amazonaws.com');
        
        if (isS3) {
          // S3 URLs: use as-is, images are already 600x600 high quality WebP
          html += `<img src="${escapeHtml(swatchSrc)}" alt="${escapeHtml(style.label)}" />`;
        } else {
          // Shopify CDN URLs: can use width params for resizing
          // Clean any existing width params first
          let cleanUrl = swatchSrc.replace(/([&?]width=\d+)/gi, '').replace(/([&?]format=\w+)/gi, '');
          // Remove trailing & or ?
          cleanUrl = cleanUrl.replace(/[&?]$/, '');
          
          // Build srcset with proper separators
          const sep = cleanUrl.includes('?') ? '&' : '?';
          const src1x = `${cleanUrl}${sep}width=100&format=webp`;
          const src2x = `${cleanUrl}${sep}width=200&format=webp`;
          const src3x = `${cleanUrl}${sep}width=300&format=webp`;
          
          html += `<img src="${escapeHtml(src3x)}" srcset="${escapeHtml(src1x)} 1x, ${escapeHtml(src2x)} 2x, ${escapeHtml(src3x)} 3x" alt="${escapeHtml(style.label)}" />`;
        }
      } else {
        html += `<span class="ring-style-picker__text">${escapeHtml(style.label)}</span>`;
      }
      html += '</button>';
    });

    html += '</div>'; // .ring-style-picker__swatches
    html += '<div class="field-error" id="rambo-ring-style-error" style="display: none; color: rgb(210, 0, 0); font-size: 0.8rem; margin-top: 0.5rem;">Please select a ring style.</div>';
    html += '</div>'; // .ring-style-picker

    // Ring Size Dropdown (using native popover structure like variant-picker)
    const popoverId = `rambo-ring-size-popover-${SECTION_ID}`;
    const modalId = `rr-ring-size-modal-${SECTION_ID}`;
    
    html += '<div class="rambo-ring-size-picker" id="rambo-ring-size-picker">';
    html += '<label class="rambo-ring-size-picker__label" style="font-weight: 600; margin-bottom: 0.25rem;">';
    html += 'Ring Size <span class="ring-style-picker__required">*</span>';
    html += '</label>';
    html += '<div class="relative" style="width: 100%;">';
    html += `<button type="button" class="select group" aria-controls="${popoverId}" aria-expanded="false" data-rr-ring-size-trigger="true" style="width: 100%;">`;
    html += `<span id="${popoverId}-selected-value">-- Select a ring size --</span>`;
    html += '<span class="animated-plus group-expanded:rotate" aria-hidden="true"></span>';
    html += '</button>';
    html += `<x-popover id="${popoverId}" class="popover popover--bottom-start color-scheme color-scheme--dialog" close-on-listbox-change style="width: 100%; max-width: 100%;">`;
    html += '<p class="h5" slot="header">Ring Size</p>';
    html += '<div class="popover__value-list" style="padding-left: 0; padding-right: 0;">';
    // Ring size chart link with ruler icon (first item) - wrap text and icon together on the right
    // Use exact same structure as size options for consistency
    html += `<label class="popover__value-option" id="rambo-ring-size-chart" style="position: relative !important; height: 61px !important; min-height: 61px !important; max-height: 61px !important; margin: 0 !important; padding: 0 1.5rem !important; display: flex !important; align-items: center !important; justify-content: flex-end !important; box-sizing: border-box !important; line-height: 1 !important; width: 100% !important; cursor: pointer !important; overflow: hidden !important; flex-shrink: 0 !important;">`;
    html += '<span style="display: flex !important; align-items: center !important; gap: 0.5rem !important; margin-left: auto !important; flex-shrink: 0 !important; line-height: 1 !important; margin: 0 !important; padding: 0 !important; height: auto !important;">';
    html += '<span style="display: inline-block !important; line-height: 1 !important; margin: 0 !important; padding: 0 !important; white-space: nowrap !important;">Ring size chart</span>';
    html += '<span class="rr-ring-size-dropdown-icon" aria-hidden="true" style="display: inline-block !important; flex-shrink: 0 !important; line-height: 1 !important; margin: 0 !important; padding: 0 !important; width: 1.5rem !important; height: 0.6rem !important;"></span>';
    html += '</span>';
    html += '</label>';
    // Size options will be populated dynamically by JavaScript
    // Use display: contents to avoid layout issues (gap/margin)
    html += '<div id="rambo-ring-size-options" style="display: contents;"></div>';
    html += '</div>'; // .popover__value-list
    
    // Ring size modal - moved outside list to avoid spacing issues
    html += `<x-modal id="${modalId}" class="modal modal--ring-size color-scheme color-scheme--dialog">`;
    html += '<span class="h5" slot="header">Ring Size Guide</span>';
    html += '<div class="prose rr-ring-size-modal">';
    html += '<p>At <strong>3TinkersPink</strong>, our rings are crafted with a comfort-fit interior, which means they usually feel about <strong>half a size larger</strong> than standard-fit rings. To make sure your ring fits perfectly, we recommend using our printable ring sizer before you order.</p>';
    html += '<h3>How to use the printable ring sizer</h3>';
    html += '<ol>';
    html += '<li><strong>Download the ring sizer</strong> using the button below.</li>';
    html += '<li><strong>Print at 100% scale.</strong> After printing, confirm the test box is correct by placing a credit card inside—it should measure exactly <strong>3.5 inches (8.89 cm)</strong>.</li>';
    html += '<li><strong>Cut out the ring sizer strip</strong>, including the small slit near the top.</li>';
    html += '<li><strong>Wrap the strip around your finger</strong> at the thickest point (usually the knuckle) and insert the pointed end through the slit to form a loop.</li>';
    html += '<li><strong>Adjust for comfort.</strong> Tighten until it is snug but can still slide over your knuckle with slight resistance.</li>';
    html += '<li><strong>Read your size.</strong> The number that lines up with the slit is your comfort-fit ring size.</li>';
    html += '</ol>';
    html += '<p><a href="/pages/ring-sizer" class="button" target="_blank" rel="noopener">Open full Ring Sizer page</a></p>';
    html += '</div>';
    html += '</x-modal>';
    
    html += '</x-popover>';
    html += '</div>'; // .relative
    html += '<div class="field-error" id="rambo-ring-size-error" style="display: none; color: rgb(210, 0, 0); font-size: 0.8rem; margin-top: 0.5rem;">Please select a ring size.</div>';
    html += '</div>'; // .rambo-ring-size-picker

    html += '</div>'; // .rambo-ring-widget__container

    widgetContainer.innerHTML = html;

    // Insert widget - if we found engraving area, insert BEFORE it; otherwise use fallback
    if (engravingArea && engravingArea.parentNode) {
      engravingArea.parentNode.insertBefore(widgetContainer, engravingArea);
    } else if (insertTarget === sectionEl) {
      // Insert at the beginning if we can't find a better target
      insertTarget.insertBefore(widgetContainer, insertTarget.firstChild);
    } else if (insertTarget) {
      // Insert before the target element
      if (insertTarget.parentNode) {
        insertTarget.parentNode.insertBefore(widgetContainer, insertTarget);
      } else {
        insertTarget.insertBefore(widgetContainer, insertTarget.firstChild);
      }
    } else {
      // Last resort: append to section
      sectionEl.appendChild(widgetContainer);
    }

    // Store ring config data on widget for later use
    widgetContainer.dataset.ringConfig = JSON.stringify(ringConfig);

    // Wire up interactions
    wireUpWidgetInteractions(ringConfig);
  }

  /**
   * Wire up widget interactions (swatch clicks, size selection)
   */
  function wireUpWidgetInteractions(ringConfig) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    const stylePicker = sectionEl.querySelector('#rambo-ring-style-picker');
    const sizePicker = sectionEl.querySelector('#rambo-ring-size-picker');
    const popoverId = `rambo-ring-size-popover-${SECTION_ID}`;
    const sizeButton = sectionEl.querySelector(`[aria-controls="${popoverId}"]`);

    if (!stylePicker || !sizePicker || !sizeButton) return;

    // Filter logic REMOVED per user request
    // All swatches will be shown by default

    // Try to restore selections from URL parameters (when coming from cart "Update" button)
    const urlParams = new URLSearchParams(window.location.search);
    const urlStyleIndex = urlParams.get('style_index');
    const urlRingSku = urlParams.get('ring_sku');
    const urlRingStyle = urlParams.get('ring_style');
    const urlRingSize = urlParams.get('ring_size');
    
    // Handle prefilter_style_code (from Collection Page) -> Most reliable auto-select
    // Format: "STYLE04" -> style index 3 (0-based)
    const prefilterStyleCode = urlParams.get('prefilter_style_code');
    if (prefilterStyleCode && !urlRingStyle) {
        // Extract number from STYLE04 -> 4 -> index 3
        const codeMatch = prefilterStyleCode.match(/STYLE[_]?(\d+)/i);
        if (codeMatch) {
            const styleNum = parseInt(codeMatch[1], 10);
            // Style numbers are 1-based, array index is 0-based
            const styleIndex = styleNum - 1;
            if (styleIndex >= 0 && styleIndex < ringConfig.styles.length) {
                widgetState.selectedStyleIndex = styleIndex;
            }
        }
    }
    
    // Handle prefilter_color (from Collection Page) -> Auto-select but don't hide
    // logic: if prefilter_color is present and ring_style (cart edit) is NOT, use prefilter to set initial selection
    if (!urlRingStyle && widgetState.selectedStyleIndex < 0) { 
        const prefilterColor = urlParams.get('prefilter_color');
        if (prefilterColor) {
            // Find match in styles
            const matchIndex = ringConfig.styles.findIndex(style => {
                const filterVal = prefilterColor.toLowerCase();
                const styleColor = (style.color || '').toLowerCase();
                const styleLabel = (style.label || '').toLowerCase();
                const styleShape = (style.shape || '').toLowerCase();
                
                return styleLabel === filterVal || 
                       styleColor === filterVal || 
                       styleShape === filterVal ||
                       (filterVal.length > 2 && styleLabel.includes(filterVal));
            });

            if (matchIndex !== -1) {
                 widgetState.selectedStyleIndex = matchIndex;
            }
        }
    }
    
    // Use shared state
    // widgetState.selectedStyleIndex and widgetState.selectedSizeCode are defined at top level
    
    // If URL has style index, SKU, or ring style/size, try to restore them
    if (urlStyleIndex || urlRingSku || urlRingStyle || urlRingSize) {
      let matchingStyleIndex = -1;
      
      // PRIORITY 1: Use exact style_index if provided (most reliable)
      if (urlStyleIndex) {
        const idx = parseInt(urlStyleIndex, 10);
        if (!isNaN(idx) && idx >= 0 && idx < ringConfig.styles.length) {
          matchingStyleIndex = idx;
        }
      }
      
      // PRIORITY 2: Match by SKU (very reliable - each variant has unique SKU)
      if (matchingStyleIndex < 0 && urlRingSku) {
        matchingStyleIndex = ringConfig.styles.findIndex(style => {
          return style.variants && style.variants.some(v => v.sku === urlRingSku);
        });
      }
      
      // PRIORITY 3: Fall back to label matching if no index or SKU match
      if (matchingStyleIndex < 0 && urlRingStyle) {
        matchingStyleIndex = ringConfig.styles.findIndex(style => {
          const cleanUrlStyle = urlRingStyle.replace(/\s+Tungsten\s*/gi, ' ').replace(/^\d{1,2}\s*[-–:]\s*/, '').trim().toLowerCase();
          const cleanStyleLabel = style.label.replace(/\s+Tungsten\s*/gi, ' ').replace(/^\d{1,2}\s*[-–:]\s*/, '').trim().toLowerCase();
          return cleanStyleLabel === cleanUrlStyle || style.label.toLowerCase() === urlRingStyle.toLowerCase();
        });
      }
      
      if (matchingStyleIndex >= 0) {
        widgetState.selectedStyleIndex = matchingStyleIndex;
        // Update size dropdown for this style
        updateSizeDropdown(ringConfig.styles[matchingStyleIndex].variants, ringConfig, matchingStyleIndex);
        
        // Find matching size and select it
        if (urlRingSize) {
          const matchingVariant = ringConfig.styles[matchingStyleIndex].variants.find(v => {
            // Match by sizeLabel or sizeCode
            return v.sizeLabel === urlRingSize || v.sizeCode === urlRingSize;
          });
          
          if (matchingVariant) {
            widgetState.selectedSizeCode = matchingVariant.sizeCode;
            // Select the radio button and update display
            setTimeout(() => {
              const radio = sectionEl.querySelector(`#${popoverId} input[value="${widgetState.selectedSizeCode}"]`);
              if (radio) {
                radio.checked = true;
                const selectedValueSpan = sectionEl.querySelector(`#${popoverId}-selected-value`);
                if (selectedValueSpan) {
                  selectedValueSpan.textContent = cleanRingSizeLabel(matchingVariant.sizeLabel);
                }
              }
            }, 100);
          }
        }
      }
    }

    // Get the selected name display element
    const selectedNameEl = sectionEl.querySelector('#rambo-ring-style-selected-name');
    
    // Function to update the selected style name display
    const updateSelectedStyleName = (styleIndex) => {
      if (selectedNameEl && styleIndex >= 0 && styleIndex < ringConfig.styles.length) {
        const style = ringConfig.styles[styleIndex];
        selectedNameEl.textContent = style.label;
      } else if (selectedNameEl) {
        selectedNameEl.textContent = '';
      }
    };

    // Style swatch click handlers
    const swatches = stylePicker.querySelectorAll('.ring-style-picker__swatch');
    swatches.forEach((swatch, index) => {
      // Set active state based on widgetState (which might be set by URL params or prefilter)
      if (index === widgetState.selectedStyleIndex) {
        swatch.classList.add('ring-style-picker__swatch--active');
        updateSelectedStyleName(index);
      } else if (widgetState.selectedStyleIndex === -1 && index === 0) {
        // Default to first style only if nothing selected yet
        swatch.classList.add('ring-style-picker__swatch--active');
        widgetState.selectedStyleIndex = 0;
        updateSelectedStyleName(0);
      }
      
      swatch.addEventListener('click', function() {
        // Update active state
        swatches.forEach(s => s.classList.remove('ring-style-picker__swatch--active'));
        swatch.classList.add('ring-style-picker__swatch--active');

        // Clear error
        const errorEl = document.getElementById('rambo-ring-style-error');
        if (errorEl) errorEl.style.display = 'none';

        const previousSizeCode = widgetState.selectedSizeCode; // Capture current size
        widgetState.selectedStyleIndex = index;
        
        // Verify the data attribute is set correctly
        if (swatch.dataset.styleIndex !== String(index)) {
          swatch.dataset.styleIndex = String(index);
        }
        
        // Update selected style name display
        updateSelectedStyleName(index);

        // Update size dropdown options (using popover structure)
        updateSizeDropdown(ringConfig.styles[index].variants, ringConfig, index);
        
        // Try to preserve size selection
        let preservedSize = false;
        if (previousSizeCode) {
           // Find equivalent variant in new style
           // Assuming sizeCode is consistent across styles (e.g. "10.5")
           const newStyleVariants = ringConfig.styles[index].variants;
           const matchingVariant = newStyleVariants.find(v => v.sizeCode === previousSizeCode);
           
           if (matchingVariant) {
             // Found match, re-select it
             widgetState.selectedSizeCode = previousSizeCode;
             preservedSize = true;
             
             // Update UI to reflect selection
             // Use slight delay to ensure dropdown DOM is ready
             setTimeout(() => {
                const radio = sectionEl.querySelector(`#${popoverId} input[value="${widgetState.selectedSizeCode}"]`);
                if (radio) {
                  radio.checked = true;
                  const selectedValueSpan = sectionEl.querySelector(`#${popoverId}-selected-value`);
                  if (selectedValueSpan) {
                    selectedValueSpan.textContent = cleanRingSizeLabel(matchingVariant.sizeLabel || matchingVariant.sizeCode);
                  }
                }
                // Update price
                updatePrice(ringConfig, index, widgetState.selectedSizeCode);
             }, 10);
           }
        }
        
        if (!preservedSize) {
           widgetState.selectedSizeCode = '';
           // Clear display
           const selectedValueSpan = sectionEl.querySelector(`#${popoverId}-selected-value`);
           if (selectedValueSpan) {
             selectedValueSpan.textContent = '-- Select a ring size --';
           }
        }
        
        // Update URL params
        updateUrlParams(ringConfig.styles[index].label, widgetState.selectedSizeCode);

        // Dispatch custom event for style change
        const event = new CustomEvent('ramboRingStyleChanged', {
          detail: {
            styleIndex: index,
            styleLabel: ringConfig.styles[index].label,
            styleCode: ringConfig.styles[index].code
          },
          bubbles: true
        });
        document.dispatchEvent(event);
        
        // DIRECT UPDATE: Find favorites button and set the style label on it
        const favoritesButton = document.querySelector('.product-gallery__favorites-button');
        if (favoritesButton) {
          favoritesButton.setAttribute('data-selected-style-label', ringConfig.styles[index].label);
        }

        // Filter gallery images based on selected style
        // Extract style number from label (e.g., "01 - Black Tungsten" -> "01")
        const styleLabel = ringConfig.styles[index].label || '';
        const styleNumberMatch = styleLabel.match(/^(\d{1,2})\s*[-–:]/);
        const styleNumber = styleNumberMatch ? styleNumberMatch[1] : null;
        if (styleNumber) {
          // Normalize to 2 digits (e.g., "1" -> "01")
          const normalizedStyleNum = styleNumber.length === 1 ? '0' + styleNumber : styleNumber;
          filterGalleryByStyle(normalizedStyleNum, sectionEl);
          updateMainHeroImage(normalizedStyleNum, sectionEl);
        } else {
          // Fallback to code if label doesn't have a number
          filterGalleryByStyle(ringConfig.styles[index].code, sectionEl);
        }
      });
    });

    // Initialize with selected style (whether from URL, prefilter, or default)
    if (widgetState.selectedStyleIndex >= 0 && widgetState.selectedStyleIndex < ringConfig.styles.length) {
      // Filter gallery for selected style
    const styleLabel = ringConfig.styles[widgetState.selectedStyleIndex].label || '';
    const styleNumberMatch = styleLabel.match(/^(\d{1,2})\s*[-–:]/);
    const styleNumber = styleNumberMatch ? styleNumberMatch[1] : null;
    
    if (styleNumber) {
      const normalizedStyleNum = styleNumber.length === 1 ? '0' + styleNumber : styleNumber;
      filterGalleryByStyle(normalizedStyleNum, sectionEl);
      // Force update the main hero image immediately
      updateMainHeroImage(normalizedStyleNum, sectionEl);
    } else {
      filterGalleryByStyle(ringConfig.styles[widgetState.selectedStyleIndex].code, sectionEl);
    }

      // If we have a size selected (e.g. from URL restore), update dropdown
      if (urlRingStyle || widgetState.selectedSizeCode) { // Check if explicit restore needed
         updateSizeDropdown(ringConfig.styles[widgetState.selectedStyleIndex].variants, ringConfig, widgetState.selectedStyleIndex);
      } else {
         // Standard init
         updateSizeDropdown(ringConfig.styles[widgetState.selectedStyleIndex].variants, ringConfig, widgetState.selectedStyleIndex);
      }
    }
    
    // Update price if both style and size are selected
    if (widgetState.selectedStyleIndex >= 0 && widgetState.selectedSizeCode) {
      updatePrice(ringConfig, widgetState.selectedStyleIndex, widgetState.selectedSizeCode);
    }
    
    // Set up modal trigger for ring size chart - find the first label in the popover
    const modalId = `rr-ring-size-modal-${SECTION_ID}`;
    const modal = sectionEl.querySelector(`#${modalId}`);
    
    // Find the first label in the popover (ring size chart)
    const ringSizeChartLabel = sectionEl.querySelector('#rambo-ring-size-chart');
    
    if (ringSizeChartLabel && modal) {
      ringSizeChartLabel.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Try multiple methods to open the modal
        if (typeof modal.show === 'function') {
           modal.show();
        } else if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else if (modal.setAttribute) {
          modal.setAttribute('open', 'true');
          // Also try setting the property directly
          modal.open = true;
        } else if (modal.open !== undefined) {
          modal.open = true;
        }
        
        // For x-modal specifically (often uses show method or open attribute)
        if (modal.tagName.toLowerCase() === 'x-modal') {
             modal.setAttribute('open', '');
             modal.style.display = 'block';
        }
      });
      
      // Listen for modal close events
      const handleModalClose = () => {
        if (ringSizeChartLabel.hasAttribute('aria-expanded')) {
          ringSizeChartLabel.setAttribute('aria-expanded', 'false');
        }
      };
      
      modal.addEventListener('close', handleModalClose);
      
      // Watch for attribute changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (!modal.hasAttribute('open')) {
              handleModalClose();
            }
          }
        });
      });
      
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }
  }

  /**
   * Update size dropdown options based on selected style (using popover structure)
   */
  function updateSizeDropdown(variants, ringConfig, styleIndex) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;
    
    const popoverId = `rambo-ring-size-popover-${SECTION_ID}`;
    const optionsContainer = sectionEl.querySelector('#rambo-ring-size-options');
    if (!optionsContainer) return;
    
    // Clear existing options
    optionsContainer.innerHTML = '';
    
    // Add options for each variant (matching native variant-picker structure)
    variants.forEach((variant, index) => {
      const optionId = `${popoverId}-option-${index}`;
      const optionEl = document.createElement('label');
      optionEl.className = 'popover__value-option';
      optionEl.setAttribute('for', optionId);
      // Set explicit inline styles - EXACT same as ring size chart label
      optionEl.style.cssText = 'position: relative !important; height: 61px !important; min-height: 61px !important; max-height: 61px !important; margin: 0 !important; padding: 0 1.5rem !important; display: flex !important; align-items: center !important; justify-content: space-between !important; box-sizing: border-box !important; line-height: 1 !important; width: 100% !important; cursor: pointer !important; overflow: hidden !important; flex-shrink: 0 !important;';
      optionEl.dataset.sizeCode = variant.sizeCode;
      optionEl.dataset.sku = variant.sku;
      optionEl.dataset.price = variant.price;
      optionEl.dataset.compareAtPrice = variant.compareAtPrice || '';
      
      // Clean up size label: remove "US" and parentheses, show only the number
      let cleanSizeLabel = cleanRingSizeLabel(variant.sizeLabel || variant.sizeCode);
      
      // Determine if we should show "Few sizes left" warning
      // Only randomly show for sizes 7 through 12.5
      let isLowInventory = false;
      const sizeNum = parseFloat(cleanSizeLabel);
      
      // STRICT CHECK: Only sizes 7 to 12.5
      if (!isNaN(sizeNum) && sizeNum >= 7 && sizeNum <= 12.5) {
        // Random chance (30%)
        isLowInventory = Math.random() < 0.3;
      } else {
        // Force false for all other sizes
        isLowInventory = false;
      }
      
      if (isLowInventory) {
        optionEl.classList.add('rr-ring-size-option-low-v2');
      }
      
      optionEl.innerHTML = `
        <input class="sr-only" type="radio" id="${optionId}" name="rambo-ring-size-radio" value="${escapeHtml(variant.sizeCode)}" data-size-code="${escapeHtml(variant.sizeCode)}" style="margin: 0 !important; padding: 0 !important;">
        <span class="rr-ring-size-value" style="display: flex !important; align-items: center !important; flex-shrink: 0 !important; line-height: 1 !important; margin: 0 !important; padding: 0 !important;">${escapeHtml(cleanSizeLabel)}</span>
        <span class="rr-ring-size-warning" style="align-items: center !important; flex-shrink: 0 !important; line-height: 1 !important; margin: 0 !important; padding: 0 !important;">Few sizes left</span>
      `;
      
      // Add click handler to select this size
      optionEl.addEventListener('click', function(e) {
        // Don't prevent default immediately - let the label click trigger the radio if possible
        // But we are handling logic manually.
        // e.preventDefault(); 
        // e.stopPropagation(); 
        
        // Note: If we remove preventDefault, clicking label toggles radio automatically.
        // But we have custom logic. Let's keep it manual for control but dispatch events.
        e.preventDefault();
        
        const radio = optionEl.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
          // Dispatch change event so x-popover knows something happened (close-on-listbox-change)
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Clear error
          const errorEl = document.getElementById('rambo-ring-size-error');
          if (errorEl) errorEl.style.display = 'none';
          
          // Update the button text (with cleaned size label)
          const selectedValueSpan = sectionEl.querySelector(`#${popoverId}-selected-value`);
          if (selectedValueSpan) {
            // Clean up size label: remove "US" and parentheses
            selectedValueSpan.textContent = cleanRingSizeLabel(variant.sizeLabel || variant.sizeCode);
          }
          
          // Close popover - aggressively
          const popover = sectionEl.querySelector(`#${popoverId}`);
          if (popover) {
            popover.removeAttribute('open');
            popover.setAttribute('aria-expanded', 'false');
            
            // Also close the button trigger
            const triggerBtn = sectionEl.querySelector(`[aria-controls="${popoverId}"]`);
            if (triggerBtn) {
               triggerBtn.setAttribute('aria-expanded', 'false');
            }
            
            // Force close by simulating click on body (click outside)
            setTimeout(() => {
              document.body.click();
              document.documentElement.click(); // Try root element too
              
              // Double tap removal
              popover.removeAttribute('open');
              if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'false');
            }, 50);
          }
          
          // Update price
          updatePrice(ringConfig, styleIndex, variant.sizeCode);
          
          // Update shared state
          widgetState.selectedSizeCode = variant.sizeCode;
          
          // Update URL params
          updateUrlParams(ringConfig.styles[styleIndex].label, variant.sizeCode);
        }
      });
      
      optionsContainer.appendChild(optionEl);
    });
  }

  /**
   * Update displayed price based on selected variant
   */
  function updatePrice(ringConfig, styleIndex, sizeCode) {
    if (!sizeCode) return;

    const style = ringConfig.styles[styleIndex];
    const variant = style.variants.find(v => v.sizeCode === sizeCode);
    if (!variant) return;

    // Find price display element and update it
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    const priceElement = sectionEl.querySelector('.price, [data-product-price], .product-price');
    if (priceElement) {
      const price = parseFloat(variant.price);
      const compareAtPrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice) : null;

      if (compareAtPrice && compareAtPrice > price) {
        priceElement.innerHTML = `
          <span class="price--compare">$${compareAtPrice.toFixed(2)}</span>
          <span class="price--on-sale">$${price.toFixed(2)}</span>
        `;
      } else {
        priceElement.textContent = `$${price.toFixed(2)}`;
      }
    }
  }

  /**
   * Override Add to Cart button to use default variant + line item properties
   */
  function overrideAddToCart(ringConfig) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;
    
    // Find the actual product area, not the whole section (which might include header)
    // Look for product-info or product-form areas specifically
    let productArea = sectionEl.querySelector('.product-info, .product, product-form, [class*="product"]');
    
    // If not found, try to find the main product container
    if (!productArea) {
      productArea = sectionEl.querySelector('product-rerender, [id*="product-info"]') || sectionEl;
    }
    
    // Final fallback: use section but exclude header
    if (!productArea || productArea === sectionEl) {
      // Create a more specific search area by excluding header
      const header = sectionEl.querySelector('header, .header');
      if (header) {
        // Search in section but not in header
        productArea = sectionEl;
      } else {
        productArea = sectionEl;
      }
    }

    // Get default variant ID from Liquid (should be exposed in a data attribute or script tag)
    const defaultVariantId = getDefaultVariantId();
    if (!defaultVariantId) {
      return;
    }

    // Find product form - try multiple selectors
    // The theme uses <product-form> custom element with a form inside
    // IMPORTANT: Don't match localization forms or other non-product forms
    let productForm = null;
    
    // Helper function to safely check if form ID/action contains a string
    const formMatches = (form, pattern) => {
      const formId = form.id || '';
      const formAction = form.action || '';
      return (typeof formId === 'string' && formId.includes(pattern)) ||
             (typeof formAction === 'string' && formAction.includes(pattern));
    };
    
    // Try finding the product-form custom element first (most reliable)
    // Search in product area, not whole section (to avoid header forms)
    const productFormElement = productArea.querySelector('product-form');
    if (productFormElement) {
      const form = productFormElement.querySelector('form');
      // Verify it's actually a product form (has name="id" input, not localization)
      if (form && form.querySelector('input[name="id"]') && 
          !formMatches(form, 'localization') &&
          (formMatches(form, 'product-form') || formMatches(form, '/cart/add') || !form.action)) {
        productForm = form;
      }
    }
    
    // Fallback: look for form with product form ID pattern
    if (!productForm) {
      const forms = productArea.querySelectorAll('form[id*="product-form"]');
      for (const form of forms) {
        if (form.querySelector('input[name="id"]') && !formMatches(form, 'localization')) {
          productForm = form;
          break;
        }
      }
    }
    
    // Fallback: look for form with cart/add action
    if (!productForm) {
      const forms = productArea.querySelectorAll('form[action*="/cart/add"]');
      for (const form of forms) {
        if (form.querySelector('input[name="id"]') && !formMatches(form, 'localization')) {
          productForm = form;
          break;
        }
      }
    }
    
    // Last resort: find form with name="id" input (product forms have this)
    // But ONLY in the product area, not in header/navigation
    if (!productForm) {
      const allForms = productArea.querySelectorAll('form');
      for (const form of allForms) {
        // Skip localization forms explicitly (check ID, action, and class)
        if (formMatches(form, 'localization') || 
            (form.action && form.action === '/localization') ||
            form.classList.contains('shopify-localization-form')) {
          continue;
        }
        // Skip forms in header/nav areas
        if (form.closest('header, nav, .header, .nav')) {
          continue;
        }
        // Product forms have input[name="id"] for variant selection with a value
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && variantInput.value && !variantInput.disabled) {
          // Double-check: product forms don't have localization in their ID
          if (!formMatches(form, 'localization')) {
            productForm = form;
            break;
          }
        }
      }
    }
    
    if (!productForm) {
      const allForms = Array.from(productArea.querySelectorAll('form'));
      
      // Last attempt: try to find ANY form that's not localization and has a variant input
      for (const form of allForms) {
        if (formMatches(form, 'localization') ||
            form.classList.contains('shopify-localization-form')) {
          continue;
        }
        
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) {
          productForm = form;
          break;
        }
      }
      
      if (!productForm) {
        return;
      }
    }
    
    // Final verification - make absolutely sure it's not a localization form
    if (formMatches(productForm, 'localization') ||
        productForm.classList.contains('shopify-localization-form')) {
      return;
    }

    // Find Add to Cart button - try multiple selectors
    const addToCartButton = productForm.querySelector('button[type="submit"], button[name="add"], .button--primary, [data-product-add-to-cart]');
    if (!addToCartButton) {
      return;
    }

    // Override form submission
    productForm.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopPropagation();
      handleCustomAddToCart(ringConfig, defaultVariantId);
      return false;
    });

    // Also handle direct button clicks (as backup)
    addToCartButton.addEventListener('click', function(e) {
      // Only prevent default if we're inside the form
      if (this.closest('form') === productForm) {
        // Don't prevent default here - let the form submit handler catch it
        // This ensures our custom handler runs
      }
    });
  }

  /**
   * Filter gallery images based on selected ring style
   * Shows only images that match the selected style code (e.g., "01", "02")
   * Also shows global images (those without style numbers in filename)
   */
  function filterGalleryByStyle(styleCode, sectionEl) {
    if (!sectionEl || !styleCode) {
      return;
    }

    // Extract numeric part from style code (e.g., "STYLE_01" -> "01", "01" -> "01", "1" -> "01")
    const normalizeStyleNumber = (codeStr) => {
      // Extract all digits from the code
      const clean = String(codeStr || '').replace(/\D/g, '');
      if (!clean) return null;
      // Normalize to 2 digits (e.g., "1" -> "01", "01" -> "01", "12" -> "12")
      if (clean.length === 1) return '0' + clean;
      return clean;
    };

    const normalizedStyleCode = normalizeStyleNumber(styleCode);
    if (!normalizedStyleCode) {
      return;
    }

    // Get style number from filename (e.g., "style05" -> "05")
    const getStyleNumberFromFilename = (filename) => {
      const lower = filename.toLowerCase();
      const match = lower.match(/style[-_]?(\d{1,2})/);
      if (match && match[1]) {
        const num = match[1];
        return num.length === 1 ? '0' + num : num;
      }
      return null;
    };

    // Get filename from image src
    const getFileNameFromImg = (img) => {
      if (!img) return '';
      const src = img.currentSrc || img.src || '';
      if (!src) return '';
      const file = src.split('/').pop().split('?')[0];
      return file.trim().toLowerCase();
    };

    // Collect all thumbnails and main media
    const collectThumbnails = () => {
      const selectors = '.product-gallery__thumbnail, .product-gallery__thumbnail-item, .product-gallery__thumb';
      let nodes = sectionEl.querySelectorAll(selectors);
      
      // Fallback: if no thumbnails, use main media
      if (!nodes.length) {
        nodes = sectionEl.querySelectorAll('.product-gallery__media');
      }

      const meta = [];
      Array.from(nodes).forEach((node) => {
        // Skip if node is detached from DOM
        if (!node || !node.parentNode) return;
        
        const img = (node.tagName.toLowerCase() === 'img') ? node : node.querySelector('img');
        if (!img) return;

        const filename = getFileNameFromImg(img);
        const styleNum = getStyleNumberFromFilename(filename);
        
        const controlNode = node;
        // Safely get data-media-id - check if methods exist
        let mediaId = null;
        try {
          if (controlNode && typeof controlNode.getAttribute === 'function') {
            mediaId = controlNode.getAttribute('data-media-id');
          }
          if (!mediaId && node && typeof node.getAttribute === 'function') {
            mediaId = node.getAttribute('data-media-id');
          }
          if (!mediaId && img) {
            const mediaContainer = img.closest('[data-media-id]');
            if (mediaContainer && typeof mediaContainer.getAttribute === 'function') {
              mediaId = mediaContainer.getAttribute('data-media-id');
            }
          }
        } catch (e) {
          // Silent fail
        }

        meta.push({
          node: controlNode,
          filename: filename,
          styleNum: styleNum, // null = global, "05" = specific
          mediaId: mediaId
        });
      });
      return meta;
    };

    // Apply filter
    const thumbnails = collectThumbnails();
    
    if (thumbnails.length === 0) {
      return;
    }
    
    let firstMatchMediaId = null;

    thumbnails.forEach((item) => {
      let shouldShow = false; // Default to HIDE - only show if explicitly matched
      const filenameLower = item.filename.toLowerCase();

      // ALWAYS SHOW: images with "ring-styles" in filename (global)
      if (filenameLower.indexOf('ring-styles') !== -1) {
        shouldShow = true;
      }
      // ALWAYS SHOW: spot images (global)
      else if (filenameLower.indexOf('spot') !== -1) {
        shouldShow = true;
      }
      else if (item.styleNum) {
        // Style-specific image: show only if it matches selected style
        shouldShow = (item.styleNum === normalizedStyleCode);
      } else {
        // Global image (no style number): HIDE by default
        shouldShow = false;
      }
      
      // Capture first style-specific match for scrolling
      if (shouldShow && item.styleNum && !firstMatchMediaId && item.mediaId) {
        firstMatchMediaId = item.mediaId;
      }

      // Toggle visibility - safely check if nodes exist before manipulating
      if (shouldShow) {
        if (item.node && item.node.parentNode) {
          try {
            item.node.style.display = '';
            item.node.removeAttribute('hidden');
          } catch (e) {
            // Silent fail
          }
        }
        if (item.mediaId) {
          try {
            const mainSelector = `.product-gallery__media[data-media-id="${item.mediaId}"]`;
            const mediaNode = sectionEl.querySelector(mainSelector);
            if (mediaNode && mediaNode.parentNode) {
              mediaNode.style.display = '';
              mediaNode.removeAttribute('hidden');
            }
          } catch (e) {
            // Silent fail
          }
        }
      } else {
        if (item.node && item.node.parentNode) {
          try {
            item.node.style.setProperty('display', 'none', 'important');
            item.node.setAttribute('hidden', '');
          } catch (e) {
            // Silent fail
          }
        }
        if (item.mediaId) {
          try {
            const mainSelector = `.product-gallery__media[data-media-id="${item.mediaId}"]`;
            const mediaNode = sectionEl.querySelector(mainSelector);
            if (mediaNode && mediaNode.parentNode) {
              mediaNode.style.setProperty('display', 'none', 'important');
              mediaNode.setAttribute('hidden', '');
            }
          } catch (e) {
            // Silent fail
          }
        }
      }
    });

    // After filtering, scroll gallery to the first visible image (the hero image)
    setTimeout(() => {
      try {
        // Find the target media element (either first match ID or first visible)
        let targetMedia = null;
        if (firstMatchMediaId) {
          targetMedia = sectionEl.querySelector(`.product-gallery__media[data-media-id="${firstMatchMediaId}"]`);
        }
        
        if (!targetMedia) {
          targetMedia = sectionEl.querySelector('.product-gallery__media:not([hidden]):not([style*="display: none"])');
        }
        
        if (targetMedia) {
          // Check if we are in a desktop slider/grid or mobile carousel
          const galleryContainer = targetMedia.closest('.product-gallery__media-list') || 
                                  targetMedia.closest('.product-gallery__carousel') ||
                                  targetMedia.closest('.product-gallery') ||
                                  targetMedia.closest('.scroller') || // Common scroller class
                                  targetMedia.parentNode; // Fallback to immediate parent
          
          if (galleryContainer) {
            // Calculate position relative to container
            const top = targetMedia.offsetTop;
            const left = targetMedia.offsetLeft;
            
            // Force smooth scroll
            galleryContainer.scrollTo({ top: top, left: left, behavior: 'smooth' });
          }
          
          // Also try standard scrollIntoView as backup
          targetMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          
          // Try clicking associated thumbnail
          if (firstMatchMediaId) {
             const thumb = sectionEl.querySelector(`.product-gallery__thumbnail[data-media-id="${firstMatchMediaId}"] button, .product-gallery__thumbnail[data-target-media-id="${firstMatchMediaId}"]`);
             if (thumb) thumb.click();
          }
        }
      } catch (e) {
        // Silent fail
      }
    }, 100); // Increased delay slightly

    // Sync dots navigation (mobile)
    try {
      const mediaNodes = sectionEl.querySelectorAll('.product-gallery__media[data-media-id]');
      const dotsNav = sectionEl.querySelector('carousel-navigation.page-dots');
      if (mediaNodes.length && dotsNav) {
        const dots = dotsNav.querySelectorAll('button.tap-area');
        Array.from(mediaNodes).forEach((mediaNode, idx) => {
          if (dots[idx]) {
            const isVisible = (mediaNode.style.display !== 'none' && !mediaNode.hasAttribute('hidden'));
            if (isVisible) {
              dots[idx].style.display = '';
              dots[idx].removeAttribute('hidden');
            } else {
              dots[idx].style.setProperty('display', 'none', 'important');
              dots[idx].setAttribute('hidden', '');
            }
          }
        });
      }
    } catch (e) {
      // Ignore errors
    }
  }

  /**
   * Get default variant ID from page
   */
  function getDefaultVariantId() {
    // Try to get from data attribute (added in main-product.liquid)
    const variantIdAttr = document.querySelector('[data-default-variant-id]')?.dataset?.defaultVariantId;
    if (variantIdAttr) return variantIdAttr;

    // Try to get from script tag with JSON (rambo-product-data)
    const ramboDataScript = document.getElementById('rambo-product-data');
    if (ramboDataScript) {
      try {
        const data = JSON.parse(ramboDataScript.textContent);
        if (data.defaultVariantId) return String(data.defaultVariantId);
      } catch (e) {
        // Silent fail
      }
    }

    // Try to get from other script tags with JSON
    const scriptTags = document.querySelectorAll('script[type="application/json"]');
    for (const script of scriptTags) {
      try {
        const data = JSON.parse(script.textContent);
        if (data.selectedVariant?.id) return String(data.selectedVariant.id);
        if (data.product?.selected_or_first_available_variant?.id) {
          return String(data.product.selected_or_first_available_variant.id);
        }
      } catch (e) {
        // Not JSON, continue
      }
    }

    // Try to get from product form
    const variantInput = document.querySelector('input[name="id"], input[name="variant_id"]');
    if (variantInput) return variantInput.value;

    return null;
  }

  /**
   * Handle custom Add to Cart with line item properties
   */
  function handleCustomAddToCart(ringConfig, defaultVariantId) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    const stylePicker = sectionEl.querySelector('#rambo-ring-style-picker');
    const popoverId = `rambo-ring-size-popover-${SECTION_ID}`;
    const selectedSizeRadio = sectionEl.querySelector(`#${popoverId} input[name="rambo-ring-size-radio"]:checked`);

    // Reset errors
    const styleError = document.getElementById('rambo-ring-style-error');
    const sizeError = document.getElementById('rambo-ring-size-error');
    if (styleError) styleError.style.display = 'none';
    if (sizeError) sizeError.style.display = 'none';

    let hasError = false;

    if (!stylePicker) {
      if (styleError) styleError.style.display = 'block';
      hasError = true;
    } else {
      const activeSwatch = stylePicker.querySelector('.ring-style-picker__swatch--active');
      if (!activeSwatch) {
        if (styleError) styleError.style.display = 'block';
        hasError = true;
      }
    }
    
    if (!selectedSizeRadio || !selectedSizeRadio.value) {
      if (sizeError) sizeError.style.display = 'block';
      hasError = true;
    }

    if (hasError) return;

    const activeSwatch = stylePicker.querySelector('.ring-style-picker__swatch--active');
    const selectedSizeCode = selectedSizeRadio.value;

    const styleIndex = parseInt(activeSwatch.dataset.styleIndex);
    if (isNaN(styleIndex) || styleIndex < 0 || styleIndex >= ringConfig.styles.length) {
      alert('Invalid ring style selection. Please try again.');
      return;
    }
    
    const styleLabel = activeSwatch.dataset.styleLabel || '';
    const style = ringConfig.styles[styleIndex];
    const variant = style.variants.find(v => v.sizeCode === selectedSizeCode);

    if (!variant) {
      alert('Selected variant not found.');
      return;
    }
    
    // Use the variant's Shopify variant ID if available, otherwise fall back to default
    // Shopify variant IDs must be numeric strings or numbers
    let variantIdToUse = defaultVariantId;
    if (variant.shopifyVariantId) {
      // Ensure it's a valid numeric string or number
      const shopifyVariantIdStr = String(variant.shopifyVariantId).trim();
      const shopifyVariantIdNum = Number(shopifyVariantIdStr);
      if (shopifyVariantIdStr && !isNaN(shopifyVariantIdNum) && shopifyVariantIdNum > 0) {
        variantIdToUse = String(shopifyVariantIdNum);
      }
    }

    // Clean sizeLabel: remove "US" and parentheses, show only the number
    let sizeLabel = cleanRingSizeLabel(variant.sizeLabel || variant.sizeCode);
    
    // Clean up style label: remove "Tungsten" from the text
    let cleanStyleLabel = styleLabel;
    if (cleanStyleLabel) {
      // Remove "Tungsten" (case-insensitive, with surrounding spaces)
      cleanStyleLabel = cleanStyleLabel.replace(/\s+Tungsten\s*/gi, ' ').trim();
      // Clean up any double spaces
      cleanStyleLabel = cleanStyleLabel.replace(/\s+/g, ' ');
    }

    // Get engraving info from the form
    // The engraving fields use section.id (the full section ID) in their IDs
    // SECTION_ID is like "shopify-section-template--23487883706685_main"
    // But section.id passed to the snippet is just "template--23487883706685_main"
    // So we need to extract the part after "shopify-section-"
    const sectionIdForEngraving = SECTION_ID.replace('shopify-section-', '');
    
    // Try multiple selectors to find engraving fields (in order of specificity)
    const engravingTextInput = sectionEl.querySelector(`#engraving-text-${sectionIdForEngraving}`) ||
                               sectionEl.querySelector('input[id*="engraving-text"]') ||
                               sectionEl.querySelector('.ring-engraving input[type="text"]') ||
                               sectionEl.querySelector('.ring-engraving__input');
    
    const engravingFontSelect = sectionEl.querySelector(`#engraving-font-select-${sectionIdForEngraving}`) ||
                                sectionEl.querySelector('select[id*="engraving-font-select"]') ||
                                sectionEl.querySelector('.ring-engraving select') ||
                                sectionEl.querySelector('.ring-engraving__select');
    
    const engravingText = engravingTextInput ? engravingTextInput.value.trim() : '';
    const engravingFont = engravingFontSelect ? engravingFontSelect.value.trim() : '';
    
    // Validate engraving (required fields)
    if (!engravingText) {
      alert('Please enter your inside engraving text.');
      if (engravingTextInput) engravingTextInput.focus();
      return;
    }
    
    if (!engravingFont) {
      alert('Please select an engraving font.');
      if (engravingFontSelect) engravingFontSelect.focus();
      return;
    }

    // Disable button during request
    const addToCartButton = sectionEl.querySelector('button[type="submit"][name="add"], button[data-product-add-to-cart], .product-form__submit');
    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.textContent = 'Adding...';
    }

    // Build properties object with ring info AND engraving
    const properties = {
      'Ring Style': cleanStyleLabel, // Use cleaned style label (without "Tungsten")
      'Ring Size': sizeLabel,
      'Ring SKU': variant.sku, // Changed from "Rambo SKU" to "Ring SKU"
      'Inside Engraving Text': engravingText,
      'Engraving Font': engravingFont,
      '_styleIndex': String(styleIndex) // Hidden property for exact style restoration
    };

    // Find the SWATCH image URL for the selected style from product JSON data
    // Use styleIndex + 1 as the style number (0-indexed array, but files are style01, style02, etc.)
    const styleNum = styleIndex + 1;
    const normalizedStyleNum = styleNum < 10 ? '0' + styleNum : String(styleNum);
    
    if (normalizedStyleNum) {
      let swatchImageUrl = null;
      
      // Get SWATCH image from rambo-product-data JSON (has full CDN URLs)
      try {
        const productDataEl = document.getElementById('rambo-product-data');
        if (productDataEl) {
          const productData = JSON.parse(productDataEl.textContent);
          const media = productData.media || [];
          
          for (const m of media) {
            if (m.src) {
              const filename = m.src.split('/').pop().split('?')[0].toLowerCase();
              // Look for SWATCH images (e.g., style01-swatch.webp)
              if (filename.includes(`style${normalizedStyleNum}-swatch`) ||
                  filename.includes(`style-${normalizedStyleNum}-swatch`) ||
                  filename.includes(`style_${normalizedStyleNum}_swatch`)) {
                swatchImageUrl = m.src;
                // Ensure https protocol
                if (swatchImageUrl.startsWith('//')) {
                  swatchImageUrl = 'https:' + swatchImageUrl;
                }
                break;
              }
            }
          }
        }
      } catch (e) {
        // Silently fail - cart will use variant image as fallback
      }
      
      // Set _image property if we found the swatch image
      if (swatchImageUrl) {
        // Remove width parameter for full resolution
        swatchImageUrl = swatchImageUrl.replace(/([&?]width=\d+)/gi, '');
        properties['_image'] = swatchImageUrl;
      }
    }

    // Add to cart via AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantIdToUse,
        quantity: 1,
        properties: properties
      })
    })
    .then(async response => {
      if (!response.ok) {
        const text = await response.text();
        let err = {};
        try {
          err = JSON.parse(text);
        } catch (e) {
          // Response was not JSON
        }
        
        let errorMessage = err.description || err.message;
        if (!errorMessage) {
          if (response.status === 409 || response.status === 422) {
             errorMessage = `Item is likely out of stock (HTTP ${response.status}).`;
          } else {
             errorMessage = `HTTP ${response.status}`;
          }
        }
        
        throw new Error(errorMessage);
      }
      return response.json();
    })
    .then(data => {
      // Show success toast
      showToast('Item added to cart successfully', 'success', 'Added to Cart');
      
      // Refresh cart count if there's a cart count element
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          // Update cart count badges
          const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #cart-count');
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count || 0;
          });
          
          // Trigger cart update event for theme
          const cartUpdateEvent = new CustomEvent('cart:updated', { detail: cart });
          document.dispatchEvent(cartUpdateEvent);
          
          // Dispatch additional events that themes might listen to
          document.dispatchEvent(new CustomEvent('cart:add', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          
          // Try multiple methods to open cart drawer
          let drawerOpened = false;
          
          // Method 1: Try cart-drawer custom element
          const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, [id*="cart-drawer"]');
          if (cartDrawer) {
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
              drawerOpened = true;
            } else if (cartDrawer.classList) {
              cartDrawer.classList.add('is-open', 'active', 'open');
              drawerOpened = true;
            }
          }
          
          // Method 2: Try Shopify theme cart API
          if (!drawerOpened && window.Shopify?.theme?.cart?.open) {
            window.Shopify.theme.cart.open();
            drawerOpened = true;
          }
          
          // Method 3: Try Drawer API (older themes)
          if (!drawerOpened && window.Shopify?.Drawer?.open) {
            window.Shopify.Drawer.open({ id: 'CartDrawer' });
            drawerOpened = true;
          }
          
          // Method 4: Try to trigger click on cart icon/button
          if (!drawerOpened) {
            const cartIcon = document.querySelector('[data-cart-icon], .cart-icon, [aria-label*="cart" i], [aria-label*="Cart" i]');
            if (cartIcon) {
              cartIcon.click();
              drawerOpened = true;
            }
          }
          
          // Method 5: Try to find and open drawer by class/attribute
          if (!drawerOpened) {
            const drawer = document.querySelector('.drawer--cart, [data-drawer="cart"], .cart-drawer');
            if (drawer) {
              drawer.classList.add('is-open', 'active', 'open');
              drawer.setAttribute('aria-hidden', 'false');
              drawerOpened = true;
            }
          }
          
          // Last resort: redirect to cart page only if drawer couldn't be opened
          if (!drawerOpened) {
            window.location.href = '/cart';
          }
        })
        .catch(err => {
          // Still try to open cart drawer even if refresh failed
          const cartDrawer = document.getElementById('cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            cartDrawer.open();
          } else if (window.Shopify?.theme?.cart?.open) {
            window.Shopify.theme.cart.open();
          } else {
            window.location.href = '/cart';
          }
        });
    })
    .catch(error => {
      showToast(error.message || 'Please try again.', 'error', 'Failed to Add to Cart');
    })
    .finally(() => {
      // Re-enable button
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = 'Add to cart';
      }
    });
  }

  /**
   * Update URL parameters to reflect current selection
   */
  function updateUrlParams(styleLabel, sizeCode) {
    try {
      const newUrl = new URL(window.location.href);
      if (styleLabel) {
        newUrl.searchParams.set('ring_style', styleLabel);
      } else {
        newUrl.searchParams.delete('ring_style');
      }
      
      if (sizeCode) {
        newUrl.searchParams.set('ring_size', sizeCode);
      } else {
        newUrl.searchParams.delete('ring_size');
      }
      
      window.history.replaceState({}, '', newUrl);
    } catch (e) {
      // Silent fail
    }
  }

  /**
   * Show a toast notification
   */
  function showToast(message, type = 'success', title = '') {
    // Remove existing toast
    const existingToast = document.querySelector('.rambo-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = `rambo-toast rambo-toast--${type}`;
    
    let iconHtml = '';
    if (type === 'success') {
      iconHtml = `<div class="rambo-toast__icon"><svg viewBox="0 0 20 20" fill="currentColor" style="color: #4caf50;"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg></div>`;
    } else {
      iconHtml = `<div class="rambo-toast__icon"><svg viewBox="0 0 20 20" fill="currentColor" style="color: #d20000;"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg></div>`;
    }

    toast.innerHTML = `
      ${iconHtml}
      <div class="rambo-toast__content">
        ${title ? `<div class="rambo-toast__title">${title}</div>` : ''}
        <div class="rambo-toast__message">${message}</div>
      </div>
    `;

    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Auto hide
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }, 4000);
  }

  /**
   * Force update the main hero image to match the selected style
   * This ensures the large displayed image matches the selection even if gallery scrolling fails
   */
  function updateMainHeroImage(styleNumber, sectionEl) {
    if (!sectionEl || !styleNumber) return;

    // Find all potential image sources
    const allImages = sectionEl.querySelectorAll('.product-gallery__media img, .product-gallery__thumbnail img');
    let heroSrc = null;

    // Look for image with matching style number and "hero" in filename
    // Pattern: style01-hero, style-01-hero, etc.
    for (const img of allImages) {
      const src = img.currentSrc || img.src || '';
      const lowerSrc = src.toLowerCase();
      const filename = lowerSrc.split('/').pop().split('?')[0];
      
      if (filename.includes(`style${styleNumber}-hero`) || 
          filename.includes(`style-${styleNumber}-hero`) ||
          filename.includes(`style_${styleNumber}_hero`)) {
        heroSrc = src;
        break;
      }
    }

    if (heroSrc) {
       // Clean width params
       heroSrc = heroSrc.replace(/([&?]width=\d+)/gi, '');
       
       // Find the main display image(s)
       // Try common selectors for the "featured" or currently visible large image
       const mainImages = sectionEl.querySelectorAll(`
         .product-gallery__media[style*="display: block"] img,
         .product-gallery__media:not([hidden]) img,
         .product-gallery__featured-media img,
         .product__media-item--featured img,
         [data-product-media-type="image"] img
       `);

       mainImages.forEach(img => {
          // Only update if it's a "main" looking image (not a small thumbnail)
          // Simple heuristic: width > 200 or it's the only one
          if (img.clientWidth > 200 || mainImages.length === 1) {
             img.src = heroSrc;
             if (img.srcset) img.srcset = heroSrc; // Nuke srcset to prevent browser reverting
          }
       });
       
       // Also look for the specifically "active" or "current" media item if using a slider
       const activeSlide = sectionEl.querySelector('.is-active .product-gallery__image img, .swiper-slide-active img');
       if (activeSlide) {
          activeSlide.src = heroSrc;
          if (activeSlide.srcset) activeSlide.srcset = heroSrc;
       }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRamboRingWidget);
  } else {
    initRamboRingWidget();
  }

  // Re-initialize on back/forward navigation (bfcache)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      // Page was restored from bfcache, need to re-initialize
      initRamboRingWidget();
    }
  });

  // Also re-initialize on dynamic content updates (for SPA themes)
  if (window.Shopify?.theme?.events) {
    window.Shopify.theme.events.subscribe('product:loaded', initRamboRingWidget);
  }

  // Widget initialized silently

})();

